/**
 * How success is measured, one evaluator per `ObjectiveKind` (03 §3). Each keeps the HUD's
 * `done / total` honest and says when the objective is complete or beyond saving.
 */

import type { Ctx } from '../ctx';
import { OBJECTIVES } from '../tuning';
import { type MissionDef, type PropDef, TICK_RATE } from '../types';
import {
  completeObjective, failObjective, inRect, isPrisoner, nearestProp, openCells, propDestroyed, propDistance,
  targetName, targetProps,
} from './common';
import { stepHoldZone, stepPlantAtTargets, stepUseObjects, stepVisitZones } from './kindsMulti';
import { stepDeliverItem, stepReachDistance } from './questItem';
import { stepKillTarget, stepLootTarget } from './target';

/** What `done / total` counts for this mission, before anything has happened. */
export function initialTotal(def: MissionDef): number {
  const kind = def.objective.kind;
  if (kind === 'destroyTargets' || kind === 'useObjects' || kind === 'plantAtTargets') {
    return Math.max(1, def.objective.targetPropIds.length);
  }
  if (kind === 'freeAndEscort') return Math.max(1, def.captives.length);
  if (kind === 'visitZones') return Math.max(1, def.objective.zones?.length ?? 0);
  // The quest item is two steps of its own: in hand, then where it belongs.
  if (kind === 'deliverItem') return 2;
  if (kind === 'reachDistance') return def.objective.questItem ? 3 : 1;
  return 1;
}

/** The thing to work: the lever if the mission has one, else the first target that is not its housing. */
export function usableProp(def: MissionDef): PropDef | null {
  const targets = targetProps(def);
  return targets.find((p) => p.kind === 'lever')
    ?? def.props.find((p) => p.kind === 'lever')
    ?? targets.find((p) => p.kind !== 'controlsPyramid')
    ?? null;
}

/** The lever sits under a concrete pyramid and cannot be reached until that is blown (01 §3, mission 8). */
export function usableLocked(ctx: Pick<Ctx, 'world' | 'statics'>, prop: PropDef): boolean {
  if (prop.kind !== 'lever') return false;
  const housing = nearestProp(ctx.statics.def, ['controlsPyramid'], prop.x, prop.z);
  return housing !== null && !propDestroyed(ctx.world, housing.prop.id);
}

function stepDestroyTargets(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const ids = statics.def.objective.targetPropIds;
  let done = 0;
  for (const id of ids) if (propDestroyed(w, id)) done++;
  w.objective.done = done;
  const needed = Math.max(1, Math.ceil(ids.length * statics.def.objective.minFraction));
  if (done >= needed) completeObjective(ctx);
}

function stepUseObject(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  if (o.used) {
    o.done = 1;
    completeObjective(ctx);
    return;
  }
  // Blow up the comms post and the code book burns with it (03 §2, mission 2).
  const prop = usableProp(statics.def);
  if (prop && propDestroyed(w, prop.id)) failObjective(ctx, 'DESTROYED BEFORE IT COULD BE USED');
}

function stepDwell(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  const zone = statics.def.objective.zone;
  if (!zone || o.complete) return;
  const r = w.ranger;
  const inside = r.status === 'active' && inRect(zone, r.x, r.z);
  // Anyone who followed him in stops the work, and it starts again from nothing.
  const interrupted = w.enemies.some((e) => e.state === 'combat' && inRect(zone, e.x, e.z));
  o.dwellTicks = inside && !interrupted ? o.dwellTicks + 1 : 0;
  if (o.dwellTicks >= (statics.def.objective.dwellSeconds ?? 0) * TICK_RATE) {
    o.done = 1;
    completeObjective(ctx);
  }
}

function stepCaptureAndEscort(ctx: Ctx): void {
  const o = ctx.world.objective;
  const prisoner = o.captives.find((c) => isPrisoner(c.kind));
  if (!prisoner) return;
  if (prisoner.state === 'boarded') {
    o.done = 1;
    completeObjective(ctx);
  } else if (prisoner.state === 'lost') failObjective(ctx, `${targetName(ctx.statics.def, 'THE OFFICER')} IS DEAD`);
}

function stepFreeAndEscort(ctx: Ctx): void {
  const o = ctx.world.objective;
  const escorts = o.captives.filter((c) => !isPrisoner(c.kind));
  o.done = escorts.filter((c) => c.state === 'boarded').length;
  // One brought out is a success; every one after that is worth more (02 §11).
  const allLost = escorts.length > 0 && escorts.every((c) => c.state === 'lost');
  if (o.done > 0) completeObjective(ctx);
  else if (allLost) failObjective(ctx, 'NO ONE LEFT TO BRING OUT');
}

function stepPlant(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  if (w.objective.complete) return;
  const standing = targetProps(statics.def).filter((t) => !propDestroyed(w, t.id));
  for (const target of standing) {
    if (w.bombs.some((b) => propDistance(target, b.x, b.z) <= OBJECTIVES.plantRadius)) {
      w.objective.done = 1;
      completeObjective(ctx);
      return;
    }
  }
  // Sabotage that goes off while he is still there is not the delayed kind.
  if (standing.length === 0) failObjective(ctx, 'THE TARGET WENT UP TOO SOON');
}

function stepReachZone(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const zone = statics.def.objective.zone;
  // Judged once, at the moment the clock runs out (03 §2, mission 11).
  if (!zone || w.clockTicks > 0 || w.objective.complete) return;
  const r = w.ranger;
  if ((r.status === 'active' || r.status === 'boarding') && inRect(zone, r.x, r.z)) {
    w.objective.done = 1;
    completeObjective(ctx);
  } else failObjective(ctx, 'NOT AT THE BORDER IN TIME');
}

/** Cells open when their prison is blown; the lever (interactions.ts) opens the tiger pits. */
export function stepCells(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  // Only worth a look on a tick when something was actually destroyed.
  if (!ctx.events.some((ev) => ev.type === 'propDestroyed')) return;
  if (!w.objective.captives.some((c) => c.state === 'held')) return;
  openCells(ctx, (c) => {
    const cell = nearestProp(statics.def, ['prison'], c.x, c.z);
    return cell !== null && cell.distance <= OBJECTIVES.cellRadius && propDestroyed(w, cell.prop.id);
  });
}

/** Is this held captive still behind something: an unkicked lever, an unblown pyramid, a standing prison? */
export function captiveLocked(ctx: Pick<Ctx, 'world' | 'statics'>, x: number, z: number): boolean {
  const { world: w, statics } = ctx;
  for (const p of statics.def.props) {
    if (p.kind === 'lever') return true;
    if (p.kind === 'controlsPyramid' && !propDestroyed(w, p.id)) return true;
  }
  const cell = nearestProp(statics.def, ['prison'], x, z);
  return cell !== null && cell.distance <= OBJECTIVES.cellRadius && !propDestroyed(w, cell.prop.id);
}

export function stepKind(ctx: Ctx): void {
  switch (ctx.statics.def.objective.kind) {
    case 'destroyTargets':
      stepDestroyTargets(ctx);
      break;
    case 'useObject':
      stepUseObject(ctx);
      break;
    case 'dwellInZone':
      stepDwell(ctx);
      break;
    case 'captureAndEscort':
      stepCaptureAndEscort(ctx);
      break;
    case 'freeAndEscort':
      stepFreeAndEscort(ctx);
      break;
    case 'plantAtTarget':
      stepPlant(ctx);
      break;
    case 'reachZone':
      stepReachZone(ctx);
      break;
    case 'useObjects':
      stepUseObjects(ctx);
      break;
    case 'plantAtTargets':
      stepPlantAtTargets(ctx);
      break;
    case 'visitZones':
      stepVisitZones(ctx);
      break;
    case 'holdZone':
      stepHoldZone(ctx);
      break;
    case 'killTarget':
      stepKillTarget(ctx);
      break;
    case 'lootTarget':
      stepLootTarget(ctx);
      break;
    case 'deliverItem':
      stepDeliverItem(ctx);
      break;
    case 'reachDistance':
      stepReachDistance(ctx);
      break;
  }
}
